var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import axios from 'axios';
import { getFriendlyErrorMessage, isJsonParsingError } from '../lib/errorHandler';
// Create axios instance with base configuration
var apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000',
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json',
    },
});
// Create specialized TTS client with extended timeout for voice processing
export var ttsApiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000',
    timeout: 180000,
    headers: {
        'Content-Type': 'application/json',
    },
});
// Add same interceptors to TTS client
ttsApiClient.interceptors.request.use(function (config) {
    // This will be enhanced with auth headers by setupAuthInterceptors
    return config;
}, function (error) {
    console.error('TTS API Request Error:', error);
    return Promise.reject(error);
});
ttsApiClient.interceptors.response.use(function (response) {
    return response;
}, function (error) {
    var _a, _b, _c, _d, _e, _f;
    console.error('TTS API Response Error:', {
        status: (_a = error.response) === null || _a === void 0 ? void 0 : _a.status,
        url: (_b = error.config) === null || _b === void 0 ? void 0 : _b.url,
        message: error.message,
        data: (_c = error.response) === null || _c === void 0 ? void 0 : _c.data,
    });
    // Handle timeout errors specifically for TTS
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        console.warn('TTS Request timed out - Chirp voices may take longer to process');
        error.isTimeout = true;
        error.userMessage = 'Voice processing is taking longer than expected. Chirp voices require additional time for high-quality synthesis. Please try again in a moment.';
    }
    else if (((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) === 429) {
        // Handle rate limiting / quota exceeded for TTS
        var errorData = (_e = error.response) === null || _e === void 0 ? void 0 : _e.data;
        error.isLimitReached = true;
        // Determine limit type from error code
        var limitType = 'requests';
        if ((errorData === null || errorData === void 0 ? void 0 : errorData.code) === 'DAILY_TOKENS_LIMIT_REACHED') {
            limitType = 'tokens';
        }
        else if ((errorData === null || errorData === void 0 ? void 0 : errorData.code) === 'DAILY_REQUESTS_LIMIT_REACHED') {
            limitType = 'requests';
        }
        else if ((errorData === null || errorData === void 0 ? void 0 : errorData.code) === 'EXAM_ANALYSIS_LIMIT_REACHED') {
            limitType = 'exam_analysis';
        }
        error.limitType = limitType;
        error.userMessage = (errorData === null || errorData === void 0 ? void 0 : errorData.error) || 'Test site limit reached. Please contact support@lecturelearn.com for assistance.';
        // Dispatch custom event for limit reached
        window.dispatchEvent(new CustomEvent('limitReached', {
            detail: {
                limitType: error.limitType,
                message: error.userMessage,
                code: errorData === null || errorData === void 0 ? void 0 : errorData.code
            }
        }));
    }
    else if (error.code === 'ERR_NETWORK') {
        error.userMessage = 'Network connection issue. Please check your internet connection and try again.';
    }
    else if (typeof ((_f = error.response) === null || _f === void 0 ? void 0 : _f.status) === 'number') {
        error.userMessage = getFriendlyErrorMessage(error);
    }
    return Promise.reject(error);
});
// Error interceptor for debugging (authentication will be added separately)
apiClient.interceptors.request.use(function (config) {
    // This will be enhanced with auth headers by setupAuthInterceptors
    return config;
}, function (error) {
    console.error('API Request Error:', error);
    return Promise.reject(error);
});
// Response interceptor for error handling
apiClient.interceptors.response.use(function (response) {
    // console.log('API Response:', {
    //   status: response.status,
    //   url: response.config.url,
    //   data: response.data,
    // });
    return response;
}, function (error) {
    var _a, _b, _c, _d, _e, _f, _g;
    console.error('API Response Error:', {
        status: (_a = error.response) === null || _a === void 0 ? void 0 : _a.status,
        url: (_b = error.config) === null || _b === void 0 ? void 0 : _b.url,
        message: error.message,
        data: (_c = error.response) === null || _c === void 0 ? void 0 : _c.data,
    });
    // JSON parsing errors: standardize handling
    if (isJsonParsingError(error)) {
        var friendly = getFriendlyErrorMessage(error);
        error.userMessage = friendly;
        error.isJsonError = true;
        window.dispatchEvent(new CustomEvent('jsonParsingError', {
            detail: { message: friendly, originalError: error.message }
        }));
        return Promise.reject(error);
    }
    // Handle specific error cases
    if (((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) === 401) {
        // Handle unauthorized - could redirect to login
        console.warn('Unauthorized request - user may need to log in again');
    }
    else if (((_e = error.response) === null || _e === void 0 ? void 0 : _e.status) === 429) {
        // Handle rate limiting / quota exceeded
        var errorData = (_f = error.response) === null || _f === void 0 ? void 0 : _f.data;
        error.isLimitReached = true;
        // Determine limit type from error code
        var limitType = 'requests';
        if ((errorData === null || errorData === void 0 ? void 0 : errorData.code) === 'DAILY_TOKENS_LIMIT_REACHED') {
            limitType = 'tokens';
        }
        else if ((errorData === null || errorData === void 0 ? void 0 : errorData.code) === 'EXAM_ANALYSIS_LIMIT_REACHED') {
            limitType = 'exam_analysis';
        }
        error.limitType = limitType;
        error.userMessage = (errorData === null || errorData === void 0 ? void 0 : errorData.error) || 'Test site limit reached. Please contact support@lecturelearn.com for assistance.';
        // Dispatch custom event for limit reached
        window.dispatchEvent(new CustomEvent('limitReached', {
            detail: {
                limitType: error.limitType,
                message: error.userMessage,
                code: errorData === null || errorData === void 0 ? void 0 : errorData.code
            }
        }));
    }
    else if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        // Handle timeout errors
        console.warn('Request timed out - this usually happens with large file processing');
        error.isTimeout = true;
        error.userMessage = getFriendlyErrorMessage(error, 'timeout');
    }
    else if (error.code === 'ERR_NETWORK') {
        // Handle network errors
        error.userMessage = getFriendlyErrorMessage(error, 'network');
    }
    else if (typeof ((_g = error.response) === null || _g === void 0 ? void 0 : _g.status) === 'number') {
        // Map remaining HTTP statuses to friendly messages
        error.userMessage = getFriendlyErrorMessage(error);
    }
    return Promise.reject(error);
});
// Special method for file uploads with extended timeout
export var uploadApiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000',
    timeout: 120000,
    headers: {
        'Content-Type': 'multipart/form-data',
    },
});
// Basic interceptors for upload client (auth will be added separately)
uploadApiClient.interceptors.request.use(function (config) {
    // This will be enhanced with auth headers by setupAuthInterceptors
    return config;
}, function (error) {
    console.error('Upload API Request Error:', error);
    return Promise.reject(error);
});
uploadApiClient.interceptors.response.use(function (response) {
    // console.log('Upload API Response:', {
    //   status: response.status,
    //   url: response.config.url,
    // });
    return response;
}, function (error) {
    var _a, _b, _c, _d, _e, _f;
    console.error('Upload API Response Error:', {
        status: (_a = error.response) === null || _a === void 0 ? void 0 : _a.status,
        url: (_b = error.config) === null || _b === void 0 ? void 0 : _b.url,
        message: error.message,
        data: (_c = error.response) === null || _c === void 0 ? void 0 : _c.data,
    });
    // JSON parsing errors: standardize handling
    if (isJsonParsingError(error)) {
        var friendly = getFriendlyErrorMessage(error);
        error.userMessage = friendly;
        error.isJsonError = true;
        window.dispatchEvent(new CustomEvent('jsonParsingError', {
            detail: { message: friendly, originalError: error.message }
        }));
        return Promise.reject(error);
    }
    // Handle rate limiting / quota exceeded (same as main apiClient)
    if (((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) === 429) {
        var errorData = (_e = error.response) === null || _e === void 0 ? void 0 : _e.data;
        error.isLimitReached = true;
        // Determine limit type from error code
        var limitType = 'requests';
        if ((errorData === null || errorData === void 0 ? void 0 : errorData.code) === 'DAILY_TOKENS_LIMIT_REACHED') {
            limitType = 'tokens';
        }
        else if ((errorData === null || errorData === void 0 ? void 0 : errorData.code) === 'EXAM_ANALYSIS_LIMIT_REACHED') {
            limitType = 'exam_analysis';
        }
        error.limitType = limitType;
        error.userMessage = (errorData === null || errorData === void 0 ? void 0 : errorData.error) || 'Test site limit reached. Please contact support@lecturelearn.com for assistance.';
        // Dispatch custom event for limit reached
        window.dispatchEvent(new CustomEvent('limitReached', {
            detail: {
                limitType: error.limitType,
                message: error.userMessage,
                code: errorData === null || errorData === void 0 ? void 0 : errorData.code
            }
        }));
    }
    else if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        error.isTimeout = true;
        error.userMessage = getFriendlyErrorMessage(error, 'timeout');
    }
    else if (typeof ((_f = error.response) === null || _f === void 0 ? void 0 : _f.status) === 'number') {
        error.userMessage = getFriendlyErrorMessage(error, 'upload');
    }
    return Promise.reject(error);
});
// Store interceptor IDs for cleanup
var apiClientAuthInterceptor = null;
var uploadApiClientAuthInterceptor = null;
var ttsApiClientAuthInterceptor = null;
// Function to set up authentication interceptors for all clients
// This will be called from AuthContext to ensure auth headers are included
export var setupAuthInterceptors = function (getToken) {
    // Remove existing auth interceptors if any
    if (apiClientAuthInterceptor !== null) {
        apiClient.interceptors.request.eject(apiClientAuthInterceptor);
    }
    if (uploadApiClientAuthInterceptor !== null) {
        uploadApiClient.interceptors.request.eject(uploadApiClientAuthInterceptor);
    }
    if (ttsApiClientAuthInterceptor !== null) {
        ttsApiClient.interceptors.request.eject(ttsApiClientAuthInterceptor);
    }
    // Set up request interceptor for main apiClient
    apiClientAuthInterceptor = apiClient.interceptors.request.use(function (config) { return __awaiter(void 0, void 0, void 0, function () {
        var token, error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!((_a = config.url) === null || _a === void 0 ? void 0 : _a.includes('/api'))) return [3 /*break*/, 4];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, getToken()];
                case 2:
                    token = _b.sent();
                    if (token) {
                        config.headers.Authorization = "Bearer ".concat(token);
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _b.sent();
                    console.error('Error getting token for request:', error_1);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/, config];
            }
        });
    }); });
    // Set up request interceptor for uploadApiClient
    uploadApiClientAuthInterceptor = uploadApiClient.interceptors.request.use(function (config) { return __awaiter(void 0, void 0, void 0, function () {
        var token, error_2;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!((_a = config.url) === null || _a === void 0 ? void 0 : _a.includes('/api'))) return [3 /*break*/, 4];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, getToken()];
                case 2:
                    token = _b.sent();
                    if (token) {
                        config.headers.Authorization = "Bearer ".concat(token);
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_2 = _b.sent();
                    console.error('Error getting token for upload request:', error_2);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/, config];
            }
        });
    }); });
    // Set up request interceptor for ttsApiClient
    ttsApiClientAuthInterceptor = ttsApiClient.interceptors.request.use(function (config) { return __awaiter(void 0, void 0, void 0, function () {
        var token, error_3;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!((_a = config.url) === null || _a === void 0 ? void 0 : _a.includes('/api'))) return [3 /*break*/, 4];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, getToken()];
                case 2:
                    token = _b.sent();
                    if (token) {
                        config.headers.Authorization = "Bearer ".concat(token);
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_3 = _b.sent();
                    console.error('Error getting token for TTS request:', error_3);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/, config];
            }
        });
    }); });
};
export default apiClient;
// ---- Convenience helpers for persistence-enabled APIs ----
export function listExamSessions() {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get('/api/sessions', { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function createExamSession(payload) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post('/api/sessions', payload, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getExamSession(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/sessions/".concat(sessionId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function saveExamAnswer(sessionId, payload) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/sessions/".concat(sessionId, "/answer"), payload, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function finishExamSession(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/sessions/".concat(sessionId, "/finish"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function deleteExamSession(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/sessions/".concat(sessionId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restartExamSession(sessionId, payload) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/sessions/".concat(sessionId, "/restart"), payload || {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function pauseExamSession(sessionId, remaining_seconds) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/sessions/".concat(sessionId, "/pause"), { remaining_seconds: remaining_seconds }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function resumeExamSession(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/sessions/".concat(sessionId, "/resume"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function listDocumentChats(session_id, document_external_id) {
    return __awaiter(this, void 0, void 0, function () {
        var params, qs, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    params = ["session_id=".concat(encodeURIComponent(session_id))];
                    if (document_external_id)
                        params.push("document_external_id=".concat(encodeURIComponent(document_external_id)));
                    qs = params.length ? "?".concat(params.join('&')) : '';
                    return [4 /*yield*/, apiClient.get("/api/chats".concat(qs), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getChatMessages(chatId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/chats/".concat(chatId, "/messages"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Document session management functions
export function getQuizQuestions(quizId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/quizzes/".concat(quizId, "/questions"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getQuizMeta(quizId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/quizzes/".concat(quizId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Document sessions (notebook-like)
export function listDocSessions() {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get('/api/doc-sessions', { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function createDocSession(payload) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post('/api/doc-sessions', payload, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function addDocumentsToSession(sessionId, externalIds) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/doc-sessions/".concat(sessionId, "/documents"), { document_external_ids: externalIds }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function listDocumentsForSession(sessionId) {
    var _a;
    return __awaiter(this, void 0, void 0, function () {
        var res, error_4;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, apiClient.get("/api/doc-sessions/".concat(sessionId, "/documents"), { withCredentials: true })];
                case 1:
                    res = _b.sent();
                    return [2 /*return*/, res.data];
                case 2:
                    error_4 = _b.sent();
                    // Handle demo session deprecation
                    if (((_a = error_4.response) === null || _a === void 0 ? void 0 : _a.status) === 410) {
                        throw new Error('Demo sessions are no longer supported. Please create a new session.');
                    }
                    throw error_4;
                case 3: return [2 /*return*/];
            }
        });
    });
}
export function deleteDocSession(sessionId, mode) {
    if (mode === void 0) { mode = 'soft'; }
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/doc-sessions/".concat(sessionId, "?mode=").concat(mode), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getSummaryById(summaryId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/summaries/".concat(summaryId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function listSessionOutputs(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/doc-sessions/".concat(sessionId, "/outputs"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function listFlashcardsBySet(setId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/flashcards?set_id=".concat(encodeURIComponent(setId)), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getUsageSummary() {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get('/api/auth/usage', { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Document session management
export function getDocSession(sessionId) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function () {
        var sessions, session, docsRes, error_5;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, listDocSessions()];
                case 1:
                    sessions = (_c.sent()).sessions;
                    session = sessions.find(function (s) { return s.id === sessionId; });
                    if (session) {
                        return [2 /*return*/, { session: session }];
                    }
                    return [4 /*yield*/, apiClient.get("/api/doc-sessions/".concat(sessionId, "/documents"), { withCredentials: true })];
                case 2:
                    docsRes = _c.sent();
                    // Create a basic session object from available data
                    return [2 /*return*/, {
                            session: {
                                id: sessionId,
                                title: "Document Session ".concat(sessionId.substring(0, 8)),
                                created_at: new Date().toISOString(),
                                updated_at: new Date().toISOString(),
                                status: 'active',
                                doc_count: ((_b = (_a = docsRes.data) === null || _a === void 0 ? void 0 : _a.documents) === null || _b === void 0 ? void 0 : _b.length) || 0
                            }
                        }];
                case 3:
                    error_5 = _c.sent();
                    console.error('Failed to get session data:', error_5);
                    throw error_5;
                case 4: return [2 /*return*/];
            }
        });
    });
}
// Chat message sending
export function sendChatMessage(payload) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post('/api/document-chat', payload, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Document upload and management
export function uploadDocument(file, sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var name, type, isPdf, isImage, isAudio, isVideo, documentType, externalId, formData, res, e_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    name = file.name.toLowerCase();
                    type = file.type.toLowerCase();
                    isPdf = name.endsWith('.pdf') || type.includes('pdf');
                    isImage = /(png|jpg|jpeg|gif|bmp|webp)$/i.test(name) || type.startsWith('image/');
                    isAudio = /(mp3|wav|m4a|ogg)$/i.test(name) || type.startsWith('audio/');
                    isVideo = /(mp4|mov|avi|mkv|webm)$/i.test(name) || type.startsWith('video/');
                    documentType = isPdf ? 'pdf' : isImage ? 'image' : isAudio ? 'audio' : isVideo ? 'video' : 'pdf';
                    externalId = "doc_".concat(Date.now(), "_").concat(Math.random().toString(36).slice(2, 8));
                    formData = new FormData();
                    formData.append('file', file);
                    formData.append('document_type', documentType);
                    formData.append('document_id', externalId);
                    return [4 /*yield*/, uploadApiClient.post('/api/upload-document', formData, {
                            withCredentials: true,
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        })];
                case 1:
                    res = _a.sent();
                    if (!sessionId) return [3 /*break*/, 5];
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 4, , 5]);
                    return [4 /*yield*/, apiClient.post("/api/doc-sessions/".concat(sessionId, "/documents"), { document_external_ids: [externalId] }, { withCredentials: true })];
                case 3:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 4:
                    e_1 = _a.sent();
                    console.warn('Failed to attach document to session', e_1);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/, __assign(__assign({}, res.data), { external_id: externalId })];
            }
        });
    });
}
// Enhanced learning features
export function generateFlashcards(sessionId, documentIds, options) {
    return __awaiter(this, void 0, void 0, function () {
        var _a, _b, numFlashcards, prompt, nodeText, customPrompt, focusAreas, difficulty, nodeId, enhancedPrompt, res;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _a = options || {}, _b = _a.numFlashcards, numFlashcards = _b === void 0 ? 10 : _b, prompt = _a.prompt, nodeText = _a.nodeText, customPrompt = _a.customPrompt, focusAreas = _a.focusAreas, difficulty = _a.difficulty, nodeId = _a.nodeId;
                    enhancedPrompt = prompt || '';
                    if (nodeText) {
                        enhancedPrompt += "\n\nFocus specifically on the topic: \"".concat(nodeText, "\"");
                    }
                    if (customPrompt) {
                        enhancedPrompt += "\n\nAdditional instructions: ".concat(customPrompt);
                    }
                    if (focusAreas && focusAreas.length > 0) {
                        enhancedPrompt += "\n\nFocus areas: ".concat(focusAreas.join(', '));
                    }
                    if (difficulty) {
                        enhancedPrompt += "\n\nDifficulty level: ".concat(difficulty);
                    }
                    return [4 /*yield*/, apiClient.post('/api/generate-flashcards', {
                            session_id: sessionId,
                            document_ids: documentIds,
                            num_flashcards: numFlashcards,
                            prompt: enhancedPrompt.trim(),
                            difficulty: difficulty,
                            node_id: nodeId,
                            nodeText: nodeText
                        }, { withCredentials: true })];
                case 1:
                    res = _c.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function generateQuiz(sessionId, documentIds, options) {
    return __awaiter(this, void 0, void 0, function () {
        var _a, _b, difficulty, _c, numQuestions, prompt, nodeText, customPrompt, focusAreas, nodeId, enhancedPrompt, res;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _a = options || {}, _b = _a.difficulty, difficulty = _b === void 0 ? 'intermediate' : _b, _c = _a.numQuestions, numQuestions = _c === void 0 ? 10 : _c, prompt = _a.prompt, nodeText = _a.nodeText, customPrompt = _a.customPrompt, focusAreas = _a.focusAreas, nodeId = _a.nodeId;
                    enhancedPrompt = prompt || '';
                    if (nodeText) {
                        enhancedPrompt += "\n\nFocus specifically on the topic: \"".concat(nodeText, "\"");
                    }
                    if (customPrompt) {
                        enhancedPrompt += "\n\nAdditional instructions: ".concat(customPrompt);
                    }
                    if (focusAreas && focusAreas.length > 0) {
                        enhancedPrompt += "\n\nFocus areas: ".concat(focusAreas.join(', '));
                    }
                    return [4 /*yield*/, apiClient.post('/api/generate-quiz', {
                            session_id: sessionId,
                            document_ids: documentIds,
                            difficulty: difficulty,
                            quiz_type: 'mcq',
                            num_questions: numQuestions,
                            prompt: enhancedPrompt.trim(),
                            node_id: nodeId,
                            nodeText: nodeText
                        }, { withCredentials: true })];
                case 1:
                    res = _d.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function generatePodcast(sessionId, documentIds, style, customPrompt, generateAudio, durationMinutes) {
    if (generateAudio === void 0) { generateAudio = true; }
    if (durationMinutes === void 0) { durationMinutes = 10; }
    return __awaiter(this, void 0, void 0, function () {
        var res, error_6;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, apiClient.post('/api/generate-podcast', {
                            session_id: sessionId,
                            document_ids: documentIds,
                            style: style || 'educational',
                            prompt: customPrompt || "Create a ".concat(style || 'educational', " podcast script with engaging dialogue and clear explanations."),
                            generate_audio: generateAudio,
                            duration_minutes: durationMinutes
                        }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    if (res.data.success && res.data.podcast) {
                        return [2 /*return*/, {
                                podcast: {
                                    id: res.data.podcast.id,
                                    title: res.data.podcast.title,
                                    description: res.data.podcast.description,
                                    transcript: res.data.podcast.transcript,
                                    audioUrl: res.data.podcast.audio_url,
                                    duration: res.data.podcast.duration_seconds,
                                    style: res.data.podcast.style,
                                    speakers: res.data.podcast.speakers || [],
                                    topicsCovered: res.data.podcast.topics_covered || [],
                                    keyTakeaways: res.data.podcast.key_takeaways || [],
                                    hasAudio: res.data.podcast.has_audio,
                                    audioGenerationError: res.data.podcast.audio_generation_error
                                }
                            }];
                    }
                    else {
                        throw new Error('Invalid response format');
                    }
                    return [3 /*break*/, 3];
                case 2:
                    error_6 = _a.sent();
                    console.error('Podcast generation failed:', error_6);
                    throw error_6;
                case 3: return [2 /*return*/];
            }
        });
    });
}
export function generateMindMap(sessionId, documentIds) {
    return __awaiter(this, void 0, void 0, function () {
        var res, error_7;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, apiClient.post('/api/generate-mindmap', {
                            session_id: sessionId,
                            document_ids: documentIds,
                            prompt: 'Generate a comprehensive mind map structure for effective learning and visualization. Focus on creating clear hierarchical relationships between concepts and include learning resources.'
                        }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, {
                            mindmap: res.data.mindmap || {
                                title: 'Document Mind Map',
                                description: 'Mind map generated from document content',
                                nodes: [],
                                difficulty: 'intermediate',
                                estimated_study_time: '15 minutes'
                            }
                        }];
                case 2:
                    error_7 = _a.sent();
                    console.error('Mind map generation failed:', error_7);
                    // Return fallback mind map structure
                    return [2 /*return*/, {
                            mindmap: {
                                title: 'Document Mind Map',
                                description: 'Mind map generation is currently unavailable. This feature is coming soon!',
                                difficulty: 'intermediate',
                                estimated_study_time: '15 minutes',
                                nodes: [
                                    {
                                        id: 'main',
                                        text: 'Main Topic',
                                        description: 'Central concept from the document',
                                        type: 'main',
                                        level: 0,
                                        x: 400,
                                        y: 300,
                                        color: '#8B5CF6',
                                        connections: ['concept1', 'concept2'],
                                        isExpanded: true,
                                        progress: 0,
                                        isBookmarked: true,
                                        document_id: 'fallback_doc',
                                        resources: []
                                    },
                                    {
                                        id: 'concept1',
                                        text: 'Key Concept 1',
                                        description: 'Important concept from the document',
                                        type: 'branch',
                                        level: 1,
                                        x: 200,
                                        y: 200,
                                        color: '#10B981',
                                        connections: [],
                                        isExpanded: true,
                                        progress: 50,
                                        isBookmarked: false,
                                        document_id: 'fallback_doc',
                                        resources: []
                                    },
                                    {
                                        id: 'concept2',
                                        text: 'Key Concept 2',
                                        description: 'Another important concept',
                                        type: 'branch',
                                        level: 1,
                                        x: 600,
                                        y: 200,
                                        color: '#F59E0B',
                                        connections: [],
                                        isExpanded: true,
                                        progress: 30,
                                        isBookmarked: false,
                                        document_id: 'fallback_doc',
                                        resources: []
                                    }
                                ],
                                layout_style: 'radial',
                                color_theme: 'vibrant',
                                key_insights: ['Document contains important concepts'],
                                learning_objectives: ['Understand key topics']
                            }
                        }];
                case 3: return [2 /*return*/];
            }
        });
    });
}
export function generateDefinitions(sessionId, documentIds) {
    return __awaiter(this, void 0, void 0, function () {
        var res, error_8;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, apiClient.post('/api/generate-summary', {
                            session_id: sessionId,
                            document_ids: documentIds,
                            summary_type: 'key_points',
                            prompt: 'Extract and define key terms and concepts from this content with clear explanations.'
                        }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, {
                            definitions: {
                                title: 'Key Definitions',
                                content: res.data.summary || res.data.response || 'Definitions generated'
                            }
                        }];
                case 2:
                    error_8 = _a.sent();
                    console.error('Definitions generation failed, using fallback:', error_8);
                    return [2 /*return*/, {
                            definitions: {
                                title: 'Key Definitions',
                                content: 'Definitions generation is currently unavailable. This feature is coming soon!'
                            }
                        }];
                case 3: return [2 /*return*/];
            }
        });
    });
}
export function generateTimeline(sessionId, documentIds) {
    return __awaiter(this, void 0, void 0, function () {
        var res, error_9;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, apiClient.post('/api/generate-summary', {
                            session_id: sessionId,
                            document_ids: documentIds,
                            summary_type: 'comprehensive',
                            prompt: 'Create a chronological timeline of events, processes, or concepts from this content.'
                        }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, {
                            timeline: {
                                title: 'Document Timeline',
                                content: res.data.summary || res.data.response || 'Timeline content generated'
                            }
                        }];
                case 2:
                    error_9 = _a.sent();
                    console.error('Timeline generation failed, using fallback:', error_9);
                    return [2 /*return*/, {
                            timeline: {
                                title: 'Document Timeline',
                                content: 'Timeline generation is currently unavailable. This feature is coming soon!'
                            }
                        }];
                case 3: return [2 /*return*/];
            }
        });
    });
}
// New session-specific endpoints to match backend changes
export function listSessionFlashcardSets(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/sessions/".concat(sessionId, "/flashcard-sets"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function listSessionQuizzes(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/sessions/".concat(sessionId, "/quizzes"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function listSessionChats(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/sessions/".concat(sessionId, "/chats"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function listSessionPodcasts(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/sessions/".concat(sessionId, "/podcasts"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getFlashcardSet(setId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/flashcard-sets/".concat(setId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getPodcast(podcastId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/podcasts/".concat(podcastId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Update existing functions to use session-based interactions
export function listInteractions(sessionId, documentExternalId) {
    return __awaiter(this, void 0, void 0, function () {
        var params, qs, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    params = ["session_id=".concat(encodeURIComponent(sessionId))];
                    if (documentExternalId)
                        params.push("document_external_id=".concat(encodeURIComponent(documentExternalId)));
                    qs = params.join('&');
                    return [4 /*yield*/, apiClient.get("/api/interactions?".concat(qs), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Soft Delete API Functions
export function softDeleteDocument(documentId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/documents/".concat(documentId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreDocument(documentId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/documents/".concat(documentId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function softDeleteFlashcardSet(setId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/flashcard-sets/".concat(setId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreFlashcardSet(setId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/flashcard-sets/".concat(setId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function softDeleteFlashcard(cardId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/flashcards/".concat(cardId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreFlashcard(cardId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/flashcards/".concat(cardId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function softDeleteQuiz(quizId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/quizzes/".concat(quizId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreQuiz(quizId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/quizzes/".concat(quizId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function softDeleteQuizQuestion(questionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/quiz-questions/".concat(questionId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreQuizQuestion(questionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/quiz-questions/".concat(questionId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function softDeleteChat(chatId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/chats/".concat(chatId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreChat(chatId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/chats/".concat(chatId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function softDeletePodcast(podcastId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/podcasts/".concat(podcastId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restorePodcast(podcastId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/podcasts/".concat(podcastId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function regeneratePodcastAudio(podcastId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/podcasts/".concat(podcastId, "/regenerate-audio"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function refreshPodcastAudioUrl(podcastId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/podcasts/".concat(podcastId, "/refresh-audio-url"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function softDeleteSummary(summaryId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/summaries/".concat(summaryId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreSummary(summaryId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/summaries/".concat(summaryId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// ---------------------------
// Mind Map Management API Functions
// ---------------------------
export function listSessionMindMaps(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/sessions/".concat(sessionId, "/mind-maps"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getMindMap(mindMapId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/mind-maps/".concat(mindMapId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function updateMindMap(mindMapId, updates) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/mind-maps/".concat(mindMapId), updates, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function createMindMapNode(mindMapId, nodeData) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/mind-maps/".concat(mindMapId, "/nodes"), nodeData, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function updateMindMapNode(nodeId, updates) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/mind-map-nodes/".concat(nodeId), updates, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function updateNodePosition(nodeId, x_position, y_position) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/mind-map-nodes/".concat(nodeId, "/position"), {
                        x_position: x_position,
                        y_position: y_position
                    }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function updateNodeProgress(nodeId, progress, mastery_level) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/mind-map-nodes/".concat(nodeId, "/progress"), {
                        progress: progress,
                        mastery_level: mastery_level
                    }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function createMindMapConnection(mindMapId, connectionData) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/mind-maps/".concat(mindMapId, "/connections"), connectionData, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function updateMindMapConnection(connectionId, updates) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/mind-map-connections/".concat(connectionId), updates, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function deleteMindMapConnection(connectionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/mind-map-connections/".concat(connectionId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function createNodeLearningActivity(nodeId, activityData) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/mind-map-nodes/".concat(nodeId, "/learning-activities"), activityData, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function listNodeLearningActivities(nodeId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/mind-map-nodes/".concat(nodeId, "/learning-activities"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Mind Map Soft Delete Functions
export function softDeleteMindMap(mindMapId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/mind-maps/".concat(mindMapId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreMindMap(mindMapId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/mind-maps/".concat(mindMapId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function softDeleteMindMapNode(nodeId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/mind-map-nodes/".concat(nodeId, "/soft-delete"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function restoreMindMapNode(nodeId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/mind-map-nodes/".concat(nodeId, "/restore"), {}, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function deleteMindMapNode(nodeId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.delete("/api/mind-map-nodes/".concat(nodeId), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Document Text-to-Speech functions
export function generateDocumentTTS(documentId, voiceName, forceRegenerate) {
    return __awaiter(this, void 0, void 0, function () {
        var requestData, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!documentId) {
                        throw new Error('Document ID is required');
                    }
                    requestData = {
                        document_id: documentId,
                        voice_name: voiceName || 'en-US-Chirp3-HD-Kore',
                        force_regenerate: forceRegenerate || false
                    };
                    console.log('Generating TTS with data:', requestData);
                    return [4 /*yield*/, ttsApiClient.post('/api/document-tts', requestData, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getDocumentTTS(documentId, voiceName) {
    return __awaiter(this, void 0, void 0, function () {
        var params, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!documentId) {
                        throw new Error('Document ID is required');
                    }
                    params = voiceName ? { voice_name: voiceName } : {};
                    console.log('Getting TTS for document:', documentId, 'with params:', params);
                    return [4 /*yield*/, apiClient.get("/api/document-tts/".concat(documentId), {
                            params: params,
                            withCredentials: true
                        })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getAvailableTTSVoices() {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    console.log('Fetching available TTS voices');
                    return [4 /*yield*/, apiClient.get('/api/document-tts/voices', { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getSessionTTS(sessionId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!sessionId) {
                        throw new Error('Session ID is required');
                    }
                    console.log('Getting all TTS for session:', sessionId);
                    return [4 /*yield*/, apiClient.get("/api/sessions/".concat(sessionId, "/tts"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function deleteDocumentTTS(documentId, voiceName) {
    return __awaiter(this, void 0, void 0, function () {
        var params, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!documentId) {
                        throw new Error('Document ID is required');
                    }
                    params = voiceName ? { voice_name: voiceName } : {};
                    console.log('Deleting TTS for document:', documentId, 'with params:', params);
                    return [4 /*yield*/, apiClient.delete("/api/document-tts/".concat(documentId), {
                            params: params,
                            withCredentials: true
                        })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function refreshTTSAudioUrl(documentId, voiceName, chunkIndex) {
    return __awaiter(this, void 0, void 0, function () {
        var data, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!documentId) {
                        throw new Error('Document ID is required');
                    }
                    data = {};
                    if (voiceName)
                        data.voice_name = voiceName;
                    if (chunkIndex !== undefined)
                        data.chunk_index = chunkIndex;
                    console.log('Refreshing TTS audio URL for document:', documentId, 'with data:', data);
                    return [4 /*yield*/, apiClient.post("/api/document-tts/".concat(documentId, "/refresh-audio-url"), data, {
                            withCredentials: true
                        })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Quiz Progress Tracking
export function createQuizProgress(quizId, data) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/quizzes/".concat(quizId, "/progress"), data, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function updateQuizProgress(progressId, data) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/quiz-progress/".concat(progressId), data, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function recordQuizResponse(progressId, data) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/quiz-progress/".concat(progressId, "/responses"), data, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Flashcard Progress Tracking - Updated for new structure
export function updateFlashcardProgress(flashcardId, data) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/flashcards/".concat(flashcardId, "/progress"), data, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getFlashcardProgress(flashcardId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/flashcards/".concat(flashcardId, "/progress"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function getFlashcardSetProgress(flashcardSetId) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.get("/api/flashcards/sets/".concat(flashcardSetId, "/progress"), { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Flashcard Set Session Tracking
export function startFlashcardSetSession(flashcardSetId, studyMode) {
    if (studyMode === void 0) { studyMode = 'practice'; }
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/flashcards/sets/".concat(flashcardSetId, "/sessions"), {
                        study_mode: studyMode
                    }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function updateFlashcardSetSession(sessionId, data) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.put("/api/flashcards/sessions/".concat(sessionId), data, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
export function completeFlashcardSetSession(sessionId, finalScore) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post("/api/flashcards/sessions/".concat(sessionId, "/complete"), {
                        final_score: finalScore
                    }, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Legacy compatibility function
export function getUserFlashcardProgress(userId, flashcardSetId) {
    return __awaiter(this, void 0, void 0, function () {
        var url, params, res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (flashcardSetId) {
                        // Use new endpoint
                        return [2 /*return*/, getFlashcardSetProgress(flashcardSetId)];
                    }
                    url = '/api/flashcards/progress';
                    params = new URLSearchParams();
                    if (userId)
                        params.append('user_id', userId);
                    if (flashcardSetId)
                        params.append('flashcard_set_id', flashcardSetId);
                    if (params.toString())
                        url += "?".concat(params.toString());
                    return [4 /*yield*/, apiClient.get(url, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
// Progress Tracking Sessions
export function createTrackingSession(data) {
    return __awaiter(this, void 0, void 0, function () {
        var res;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiClient.post('/api/progress/tracking-sessions', data, { withCredentials: true })];
                case 1:
                    res = _a.sent();
                    return [2 /*return*/, res.data];
            }
        });
    });
}
